import React, { useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { submitDataRequest } from '../utils/api';
import { PRIVACY_CONTACT } from '../utils/dpdp';

const REQUEST_TYPES = [
  { value: 'access', label: 'Access my data (get a summary of data you hold)' },
  { value: 'correction', label: 'Correct / update my data' },
  { value: 'erasure', label: 'Erase / delete my data' },
  { value: 'withdraw_consent', label: 'Withdraw my consent' },
  { value: 'nomination', label: 'Nominate someone to exercise my rights' },
  { value: 'grievance', label: 'Raise a grievance / complaint' },
];

const DataRequest = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', request_type: 'access', details: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error('Please confirm the declaration to submit.'); return; }
    if (!form.name.trim() || !form.email.trim()) { toast.error('Please enter your name and email.'); return; }
    setSubmitting(true);
    try {
      await submitDataRequest({ ...form, consent });
      setDone(true);
    } catch (err) {
      toast.error('Could not submit: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="data-request-page">
      <SEO
        title="Data Rights Request | Rubicon Finservices"
        description="Exercise your data rights under the DPDP Act 2023 — request access, correction, erasure, consent withdrawal, nomination or raise a grievance."
        keywords="DPDP data request, data rights, erasure, access, grievance, Rubicon Finservices"
      />
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-14">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck size={30} className="text-amber-300" />
            <h1 className="text-3xl sm:text-4xl font-bold">Data Rights Request</h1>
          </div>
          <p className="text-blue-100 text-sm md:text-base">Under the Digital Personal Data Protection Act, 2023, you can ask us to access, correct or erase your data, withdraw consent, nominate a representative, or raise a grievance. We respond within 90 days.</p>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-2xl py-12">
        {done ? (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 text-center" data-testid="data-request-success">
            <CheckCircle className="mx-auto text-green-600 mb-4" size={56} />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Request received</h2>
            <p className="text-slate-600">Thank you. Our Grievance Officer will review your request and respond to <strong>{form.email}</strong> within the timeframe required under the DPDP Act (up to 90 days).</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border-2 border-slate-200 rounded-2xl p-6 md:p-8 space-y-5" data-testid="data-request-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dr-name">Full Name *</Label>
                <Input id="dr-name" data-testid="dr-name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="dr-email">Email *</Label>
                <Input id="dr-email" type="email" data-testid="dr-email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
              </div>
            </div>
            <div>
              <Label htmlFor="dr-phone">Phone (optional)</Label>
              <Input id="dr-phone" data-testid="dr-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="dr-type">What would you like to do? *</Label>
              <select
                id="dr-type"
                data-testid="dr-type"
                value={form.request_type}
                onChange={(e) => set('request_type', e.target.value)}
                className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {REQUEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="dr-details">Details</Label>
              <Textarea id="dr-details" data-testid="dr-details" rows={4} value={form.details} onChange={(e) => set('details', e.target.value)} placeholder="Tell us more about your request (e.g. what data to correct or erase)." />
            </div>

            <label className="flex items-start gap-3 cursor-pointer bg-slate-50 border border-slate-200 rounded-lg p-4">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 text-green-600" data-testid="dr-consent" />
              <span className="text-sm text-slate-700">I confirm the above information is accurate and I am the Data Principal (or their authorised representative). I consent to Rubicon Finservices processing this request. <span className="text-red-500">*</span></span>
            </label>

            <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="dr-submit">
              {submitting ? 'Submitting…' : 'Submit Request'}
            </Button>
            <p className="text-xs text-slate-500 text-center">Or email us directly at <a href={`mailto:${PRIVACY_CONTACT.grievanceEmail}`} className="text-blue-600 underline">{PRIVACY_CONTACT.grievanceEmail}</a>.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default DataRequest;
